import { useEffect, useState } from "react";
import { logger } from "@/lib/logger";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { BomImage } from "./BomImage";
import { printPickList } from "./pickList";
import { Layers, PackageOpen, AlertCircle, PackageX, Printer, ShoppingBag } from "lucide-react";
import { fmtQty } from "@/lib/constants";

// Flatten the nested recipe tree iteratively (recursive JSX components break the build tooling).
function flattenTree(root) {
  const rows = [];
  const stack = [{ node: root, depth: 0 }];
  while (stack.length) {
    const { node, depth } = stack.pop();
    rows.push({ node, depth });
    for (let i = node.components.length - 1; i >= 0; i--) {
      stack.push({ node: node.components[i], depth: depth + 1 });
    }
  }
  return rows;
}

function TreeRows({ root }) {
  const rows = flattenTree(root);
  return (
    <div>
      {rows.map(({ node, depth }, idx) => {
        const manufactured = node.item_type === "manufactured";
        return (
          <div
            key={`${node.item_id}-${idx}`}
            className={`flex items-center gap-2.5 py-2 rounded-md ${manufactured ? "bg-blue-50/60" : ""}`}
            style={{ paddingLeft: depth * 18 + 6, paddingRight: 6 }}
            data-testid={`materials-tree-node-${node.sku}`}
          >
            <BomImage src={node.image_url} alt={node.name} className="h-9 w-9" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-slate-800 truncate">{node.name}</p>
              <p className="text-[10px] text-slate-400 font-mono-data">
                {node.sku} · <span className={manufactured ? "text-blue-600" : "text-slate-400"}>{node.item_type}</span>
              </p>
            </div>
            <span className="text-xs font-mono-data font-bold text-slate-700 whitespace-nowrap">
              {fmtQty(node.required_qty, node.unit)} {node.unit}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function MaterialsSheet({ order, onClose, onViewSalesOrder }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (order) {
      setData(null);
      api.get(`/orders/${order.id}/materials`).then(setData).catch((e) => {
        logger.error("Failed to load material requirements:", e);
        setData({ has_bom: false, error: true });
      });
    }
  }, [order]);

  return (
    <Sheet open={!!order} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="materials-sheet">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            <Layers className="h-4 w-4 text-blue-600" />
            Material Requirements
          </SheetTitle>
          <SheetDescription className="font-mono-data text-xs">
            {order?.order_code} · {order?.product_name} · {order?.target_quantity?.toLocaleString("en-IN")} units
          </SheetDescription>
        </SheetHeader>

        {order?.sales_order_id && (
          <button
            type="button"
            onClick={() => onViewSalesOrder?.(order)}
            className="mt-4 w-full flex items-center gap-2.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2.5 text-left hover:bg-indigo-100 transition-colors"
            data-testid="materials-so-link"
          >
            <ShoppingBag className="h-4 w-4 text-indigo-600 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-wide text-indigo-400 font-semibold">Fulfils sales order</p>
              <p className="text-xs font-semibold text-indigo-800 font-mono-data truncate">{order.so_number || "SO"} · {order.customer_name || "Customer"}</p>
            </div>
            <span className="text-[10px] text-indigo-600 font-semibold whitespace-nowrap">View →</span>
          </button>
        )}

        {!data ? (
          <p className="text-sm text-slate-400 py-8 text-center">Exploding recipe…</p>
        ) : !data.has_bom ? (
          <div className="mt-8 flex flex-col items-center gap-2 text-center" data-testid="materials-no-bom">
            <AlertCircle className="h-8 w-8 text-slate-300" />
            <p className="text-sm text-slate-500 font-medium">No BOM linked to this order</p>
            <p className="text-xs text-slate-400 max-w-[260px]">
              Edit the order and pick a BOM product, or create a recipe in the BOM tab.
            </p>
          </div>
        ) : (
          <div className="mt-5 space-y-6">
            {data.shortage && (
              <div className="rounded-lg border border-red-200 bg-red-50/70 px-3 py-2.5 flex items-start gap-2" data-testid="materials-shortage-banner">
                <PackageX className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                <p className="text-xs text-red-700">
                  <span className="font-semibold">Material shortage.</span> Current stock cannot cover the remaining units of this order — restock the highlighted materials.
                </p>
              </div>
            )}
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2 flex items-center gap-1.5">
                <PackageOpen className="h-3.5 w-3.5" /> Nested recipe breakdown
              </p>
              <div className="rounded-lg border border-slate-200 p-1.5" data-testid="materials-tree">
                <TreeRows root={data.tree} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                  Flattened totals · stock coverage (remaining units)
                </p>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => printPickList(data)} data-testid="print-pick-list-btn">
                  <Printer className="h-3 w-3 mr-1" /> Print Pick List
                </Button>
              </div>
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-50" data-testid="materials-totals">
                {data.totals.map((t) => {
                  const short = (t.missing || 0) > 0;
                  return (
                    <div key={t.sku} className={`flex items-center gap-2.5 px-3 py-2 ${short ? "bg-red-50/60" : ""}`} data-testid={`materials-total-${t.sku}`}>
                      <BomImage src={t.image_url} alt={t.name} className="h-8 w-8" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-slate-800 truncate">{t.name}</p>
                        <p className="text-[10px] text-slate-400 font-mono-data">
                          {t.sku} · in stock: <span className={short ? "text-red-600 font-semibold" : "text-emerald-600 font-semibold"}>{fmtQty(t.in_stock, t.unit)} {t.unit}</span>
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`text-sm font-mono-data font-bold whitespace-nowrap ${short ? "text-red-700" : "text-blue-700"}`}>
                          {fmtQty(t.required_remaining, t.unit)} {t.unit}
                        </span>
                        {short && (
                          <p className="text-[10px] font-mono-data text-red-600 font-semibold" data-testid={`materials-short-${t.sku}`}>
                            short {fmtQty(t.missing, t.unit)} {t.unit}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
